import React, { useState, useCallback } from 'react';
import { Tabs, Button, Select, message } from 'antd';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import ExceptionOrderFilter from './components/ExceptionOrderFilter';
import ExceptionOrderTable from './components/ExceptionOrderTable';
import {
    ExceptionTab,
    ExceptionOrderFilters as FilterType,
    ExceptionSubTab,
    ExceptionOrderListItem,
} from '../../../graphql/types/ExceptionOrderTypes';
import { QUERY_EXCEPTION_ORDERS_BY_TAB } from './graphql/queries/exceptionOrderQueries';
import { QueryExceptionOrdersByTabResponse } from '../../../graphql/types/ExceptionOrderTypes';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { SiteType } from '../../../graphql/types/SiteType';
import { BATCH_SET_NEXT_ACTION, BatchSetNextActionResponse, BatchSetNextActionVariables } from '../../wms/exceptions/graphql/mutations/setNextAction';
import { useExceptionOrderCounts } from './hooks/useExceptionOrderCounts';
import {
    GENERATE_EXCEPTION_ORDER_DOWNLOAD_REPORT,
    GenerateExceptionOrderDownloadReportResponse,
    GenerateExceptionOrderDownloadReportVariables
} from './graphql/mutations/generateExceptionOrderDownloadReport';
import ParcelsDownloadModal from '../../central/parcels/components/ParcelsDownloadModal';
import { getUserInfo } from '../../../utils/auth';
import { MARK_AS_EXCEPTION } from '../../../graphql/mutations/markAsException';
import BatchMarkAsExceptionModal, { BatchMarkExceptionInput } from '../../../components/BatchMarkAsExceptionModal';
import { GET_ALL_EXCEPTION, ExceptionDictionaryType } from '../../delivery/parcels/graphql/queries/getAllException';
import { filterExceptionByUserRole } from '../../../config/roleExceptionConfig';
import CentralParcelDetailDrawer from '../../central/parcels/components/CentralParcelDetailDrawer';
import { ACTION_NAME_MAP, nextActionOptions } from '../../../config/filterOptions';

import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';
import { showError } from '../../../utils/exceptionUtils';
import { useDownloadCooldown } from '../../../hooks/useDownloadCooldown';

const { TabPane } = Tabs;

/**
 * 获取Tab标签文本，包含计数和加载状态
 */
const getTabLabel = (baseLabel: string, count: number | undefined, loading: boolean = false): string => {
    if (loading) {
        return `${baseLabel} (...)`;
    }
    // 始终显示计数，包括0
    const displayCount = count ?? 0;
    return `${baseLabel} (${displayCount})`;
};

/**
 * 获取带小红点的Tab标签（用于待下架tab）
 */
const getTabLabelWithBadge = (baseLabel: string, count: number | undefined, loading: boolean = false): React.ReactNode => {
    if (loading) {
        return `${baseLabel} (...)`;
    }
    const displayCount = count ?? 0;
    const labelText = `${baseLabel} (${displayCount})`;
    
    // 当count > 0时显示小红点
    if (displayCount > 0) {
        return (
            <span style={{ position: 'relative', display: 'inline-block' }}>
                {labelText}
                <span
                    style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-10px',
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#ff4d4f',
                        borderRadius: '50%',
                        boxShadow: '0 0 0 2px #fff',
                    }}
                />
            </span>
        );
    }
    
    return labelText;
};

const ExceptionOrderPage: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const { getNextActionTextFromBackend } = useStatusInternationalization();

    // 当前一级Tab
    const [activeTab, setActiveTab] = useState<string>(ExceptionTab.WAREHOUSE_SUSPECTED_LOST);
    // 当前二级Tab
    const [activeSubTab, setActiveSubTab] = useState<ExceptionSubTab | undefined>(undefined);

    // 选中的行
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ExceptionOrderListItem[]>([]);

    // 批量设置的动作
    const [batchActionValue, setBatchActionValue] = useState<string | undefined>(undefined);

    // 异步下载相关状态
    const { downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime } = useDownloadCooldown(`exceptionOrderDownloadLastTime_${getUserInfo()?.account?.id}`);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState<string>('');
    const [generateReportMutation] = useMutation<GenerateExceptionOrderDownloadReportResponse, GenerateExceptionOrderDownloadReportVariables>(GENERATE_EXCEPTION_ORDER_DOWNLOAD_REPORT);

    // 当前操作仓ID，用于查询/计数接口的仓库级权限校验（@RequireResourceAndWarehouse）
    const warehouseId = parseInt(currentWarehouse?.id ?? '0', 10);

    // 计数 Hook - 使用重构后的版本
    const { tabCounts, loadingCounts, refreshAllCounts, updateCurrentTabCount, refreshSubTabCounts } = useExceptionOrderCounts(warehouseId);

    // 批量设置 mutation
    const [batchSetNextAction, { loading: batchSetLoading }] = useMutation<BatchSetNextActionResponse, BatchSetNextActionVariables>(BATCH_SET_NEXT_ACTION);

    // 标记异常相关状态
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);

    // 包裹详情抽屉状态
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailOrderId, setDetailOrderId] = useState<string>('');

    // 获取用户角色和异常字典数据
    const userRole = getUserInfo()?.account?.role?.name || '';
    const { data: exceptionData } = useQuery(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, currentWarehouse?.siteType);

    // 筛选条件（默认最近14天）
    const [filters, setFilters] = useState<FilterType>(() => {
        const fourteenDaysAgo = dayjs().subtract(14, 'day');
        return {
            exceptionStatus: [10, 50], // 默认选中新建和已处理状态
            // 不设置默认的 overallStatus，让后端根据tab/subTab预设
            pageNumber: 0,
            pageSize: 20,
            tab: ExceptionTab.WAREHOUSE_SUSPECTED_LOST, // 枚举值，会自动转换为字符串
            markExceptionAtFrom: fourteenDaysAgo.startOf('day').unix(),
            markExceptionAtTo: dayjs().endOf('day').unix(),
        };
    });

    // GraphQL 查询 - 使用 useLazyQuery 手动触发
    const [executeQuery, { data, loading }] = useLazyQuery<QueryExceptionOrdersByTabResponse>(
        QUERY_EXCEPTION_ORDERS_BY_TAB,
        {
            fetchPolicy: 'cache-and-network', // 修复：使用cache-and-network策略，确保数据更新
            errorPolicy: 'all', // 即使有错误也返回部分数据
        }
    );

    // 当新数据加载完成时，手动更新当前tab的计数
    const handleDataLoaded = (currentTabCount: number) => {
        // 关键修复：只有在查询主tab（无子tab）或查询"全部"子tab时才更新主tab计数
        // 对于OTHER_EXCEPTIONS，不应该用子tab的计数更新主tab，因为主tab应该是所有子tab的总和
        const shouldUpdateMainTab = !activeSubTab || 
            (activeTab === 'DRIVER_RETURN' && activeSubTab === 'all');
        
        if (shouldUpdateMainTab) {
            updateCurrentTabCount(activeTab, currentTabCount);
        }
    };

    // 使用数据：显示最新的数据
    const response = data?.queryExceptionOrdersByTab;

    // 手动触发数据加载完成处理
    React.useEffect(() => {
        if (data?.queryExceptionOrdersByTab && !loading) {
            const currentTabCount = data.queryExceptionOrdersByTab.totalCount || 0;
            handleDataLoaded(currentTabCount);
        }
    }, [data, loading]); // 简化依赖，只监听数据和加载状态

    // 下一步动作选项（使用统一配置，过滤掉赔偿选项，与旧页面保持一致）
    const ACTION_OPTIONS = nextActionOptions
        .filter(opt => opt.value !== 4) // 过滤掉赔偿选项（value=4）
        .map(opt => ({
            value: String(opt.value),
            label: opt.label
        }));

    // 手动初始化计数 - 在组件挂载时调用
    const initializeCounts = () => {
        if (currentWarehouse) {
            const fourteenDaysAgo = dayjs().subtract(14, 'day');
            const initialFilters: FilterType = {
                exceptionStatus: [10, 50], // 默认选中新建和已处理状态
                pageNumber: 0,
                pageSize: 20,
                markExceptionAtFrom: fourteenDaysAgo.startOf('day').unix(),
                markExceptionAtTo: dayjs().endOf('day').unix(),
            };
            
            // 手动刷新主tab计数
            refreshAllCounts(initialFilters, activeTab);

            // 当前tab有子tab时才刷新子tab计数（hook 内部对无子tab的主tab直接跳过）
            refreshSubTabCounts(initialFilters, activeTab);
        }
    };

    // 组件挂载时手动初始化
    React.useEffect(() => {
        initializeCounts();
    }, []); // 只在组件挂载时执行一次

    // 当仓库切换时，重置所有状态
    React.useEffect(() => {
        if (currentWarehouse) {
            // 重置所有状态到初始值
            const fourteenDaysAgo = dayjs().subtract(14, 'day');
            
            // 1. 重置tab状态
            setActiveTab(ExceptionTab.WAREHOUSE_SUSPECTED_LOST);
            setActiveSubTab(undefined);
            
            // 2. 重置filters为默认值（包含新仓库的分拣码）
            const defaultFilters: FilterType = {
                exceptionStatus: [10, 50],
                pageNumber: 0,
                pageSize: 20,
                tab: ExceptionTab.WAREHOUSE_SUSPECTED_LOST,
                markExceptionAtFrom: fourteenDaysAgo.startOf('day').unix(),
                markExceptionAtTo: dayjs().endOf('day').unix(),
            };
            setFilters(defaultFilters);
            
            // 3. 清空选中状态
            setSelectedRowKeys([]);
            setSelectedRows([]);
            setBatchActionValue(undefined);
            
            // 4. 清空详情抽屉
            setDetailVisible(false);
            setDetailOrderId('');
            
            // 5. 重置计数状态（显示加载中）
            // tab 已重置为第一个（无子tab），故无需刷新任何子tab计数
            refreshAllCounts(defaultFilters, ExceptionTab.WAREHOUSE_SUSPECTED_LOST);
        }
    }, [currentWarehouse?.id]); // 仅监听仓库ID变化

    // Tab 切换时手动处理状态更新
    const handleTabChangeManual = (key: string) => {
        // 更新tab状态
        setActiveTab(key);
        
        // 根据一级tab设置默认的二级tab
        let newSubTab: ExceptionSubTab | undefined;
        if (key === ExceptionTab.OTHER_EXCEPTIONS) {
            newSubTab = 'missing' as ExceptionSubTab;
        } else if (key === ExceptionTab.DRIVER_RETURN) {
            newSubTab = 'all' as ExceptionSubTab;
        } else {
            newSubTab = undefined;
        }
        setActiveSubTab(newSubTab);
        
        // 关键修复：刷新计数时使用清理后的filters，移除tab特定的筛选条件
        // 这样可以避免前一个tab的条件影响新tab的计数
        const cleanFiltersForCount = {
            trackingNumbers: filters.trackingNumbers,
            clientId: filters.clientId,
            exceptionStatus: filters.exceptionStatus,
            // nextActions: filters.nextActions,  // 移除：让后端根据tab/subTab补充预设
            markExceptionAtFrom: filters.markExceptionAtFrom,
            markExceptionAtTo: filters.markExceptionAtTo,
            returnToWhsAtFrom: filters.returnToWhsAtFrom,
            returnToWhsAtTo: filters.returnToWhsAtTo,
            putawayAtFrom: filters.putawayAtFrom,
            putawayAtTo: filters.putawayAtTo,
            warehouseLocationCode: filters.warehouseLocationCode,
            storageNumber: filters.storageNumber,
            // 不包含 overallStatus、exceptionReasonCodes、nextActions，让后端根据tab自动补充
        };
        
        // 手动刷新计数：主tab计数 1 个请求；只查新展开主tab的子tab（其它主tab子tab标签不可见）
        refreshAllCounts(cleanFiltersForCount, key);
        refreshSubTabCounts(cleanFiltersForCount, key);
    };

    // 通用函数：从filters构建cleanFilters并刷新计数
    // 关键原则：计数查询只使用通用条件，不包含tab特定条件（overallStatus、exceptionReasonCodes、nextActions）
    // 让后端根据tab自动补充预设条件
    const refreshCountsWithFilters = useCallback((sourceFilters: FilterType) => {
        try {
            const cleanFilters = {
                trackingNumbers: sourceFilters.trackingNumbers,
                // 不包含 overallStatus - 让后端根据tab补充
                clientId: sourceFilters.clientId,
                exceptionStatus: sourceFilters.exceptionStatus,
                // 不包含 exceptionReasonCodes - 让后端根据tab补充
                // 不包含 nextActions - 让后端根据tab/subTab补充
                markExceptionAtFrom: sourceFilters.markExceptionAtFrom,
                markExceptionAtTo: sourceFilters.markExceptionAtTo,
                returnToWhsAtFrom: sourceFilters.returnToWhsAtFrom,
                returnToWhsAtTo: sourceFilters.returnToWhsAtTo,
                putawayAtFrom: sourceFilters.putawayAtFrom,
                putawayAtTo: sourceFilters.putawayAtTo,
                warehouseLocationCode: sourceFilters.warehouseLocationCode,
                storageNumber: sourceFilters.storageNumber,
            };

            refreshAllCounts(cleanFilters, activeTab);
            // 仅当前展开主tab的子tab需要计数；无子tab的主tab在 hook 内部直接跳过
            refreshSubTabCounts(cleanFilters, activeTab);
        } catch (error) {
            console.error('Failed to refresh counts with filters:', error);
            // 即使计数刷新失败，也不影响主要功能
        }
    }, [refreshAllCounts, refreshSubTabCounts, activeTab]);

    // 处理筛选
    const handleFilter = () => {
        const filterParams = {
            ...filters,
            tab: activeTab,
            subTab: activeSubTab,
        };
        executeQuery({
            variables: {
                warehouseId,
                filters: filterParams,
            },
        });

        // 关键修复：筛选后同时更新所有tab计数
        refreshCountsWithFilters(filters);
    };

    // 重置筛选
    const handleResetFilters = () => {
        // 所有 tab 默认最近14天
        const fourteenDaysAgo = dayjs().subtract(14, 'day');
        const defaultFilters: FilterType = {
            exceptionStatus: [10, 50],
            pageNumber: 0,
            pageSize: 20,
            tab: activeTab,
            markExceptionAtFrom: fourteenDaysAgo.startOf('day').unix(),
            markExceptionAtTo: dayjs().endOf('day').unix(),
        };

        setFilters(defaultFilters);
        const filterParams = {
            ...defaultFilters,
            tab: activeTab,
            subTab: activeSubTab,
        };
        executeQuery({
            variables: {
                warehouseId,
                filters: filterParams,
            },
        });

        // 关键修复：重置后同时更新所有tab计数
        refreshCountsWithFilters(defaultFilters);
    };

    // 分页变化
    const handlePaginationChange = (page: number, pageSize: number) => {
        const newFilters = {
            ...filters,
            pageNumber: page - 1, // Ant Design 的页码从 1 开始，后端从 0 开始
            pageSize: pageSize,
        };
        setFilters(newFilters);
        const filterParams = {
            ...newFilters,
            tab: activeTab,
            subTab: activeSubTab,
        };
        executeQuery({
            variables: {
                warehouseId,
                filters: filterParams,
            },
        });
    };

    // 一级 Tab 切换
    const handleTabChange = (key: string) => {
        handleTabChangeManual(key);
    };

    // 手动处理tab切换后的数据更新
    const updateFiltersAndQuery = (newTab: string, newSubTab: ExceptionSubTab | undefined) => {
        const updatedFilters: FilterType = {
            // 保留通用筛选项
            trackingNumbers: filters.trackingNumbers,
            // 切换tab时清空overallStatus，让后端根据tab/subTab预设
            overallStatus: undefined,
            clientId: filters.clientId,
            // 保留异常状态的选择，如果没有则使用默认值
            exceptionStatus: filters.exceptionStatus || [10, 50],
            // 切换tab时清空异常原因码，让后端根据tab补充
            exceptionReasonCodes: undefined,
            // 切换tab时清空下一步动作，让后端根据tab/subTab预设
            nextActions: undefined,
            markExceptionAtFrom: filters.markExceptionAtFrom,
            markExceptionAtTo: filters.markExceptionAtTo,
            returnToWhsAtFrom: filters.returnToWhsAtFrom,
            returnToWhsAtTo: filters.returnToWhsAtTo,
            // 重置分页和 Tab 相关
            pageNumber: 0,
            pageSize: filters.pageSize || 20,
            tab: newTab,
            // 清空 Tab 特有的筛选项
            warehouseLocationCode: undefined,
            storageNumber: undefined,
            putawayAtFrom: undefined,
            putawayAtTo: undefined,
        };

        setFilters(updatedFilters);
        // 清空选中状态
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setBatchActionValue(undefined);
        
        // 关键修复：手动触发查询，使用 network-only 策略强制从服务器获取最新数据
        executeQuery({
            variables: {
                warehouseId,
                filters: {
                    ...updatedFilters,
                    tab: newTab,
                    subTab: newSubTab,
                },
            },
            fetchPolicy: 'network-only', // 强制从网络获取最新数据，不使用缓存
        });
    };

    // 当tab状态改变时，手动更新数据
    React.useEffect(() => {
        if (currentWarehouse) {
            updateFiltersAndQuery(activeTab, activeSubTab);
        }
    }, [activeTab, activeSubTab, currentWarehouse?.id]); // 监听tab状态和仓库变化

    // 二级 Tab 切换
    const handleSubTabChange = (key: string) => {
        const newSubTab = key as ExceptionSubTab;
        setActiveSubTab(newSubTab);

        // 清空选中状态，避免跨tab选中
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setBatchActionValue(undefined);

        // updateFiltersAndQuery 由 useEffect 监听 activeSubTab 变化自动触发，无需显式调用

        // 子tab切换时刷新子tab计数
        //
        // 计数查询不含 subTab 参数，入参与切换前一致，但仓库作业场景下同一批包裹会被并发操作
        // （如在「待上架」扫码上架后，该包裹流向「已上架」），不重查会让用户看到进页面那一刻的陈旧值。
        // 使用清理后的filters，移除tab特定的筛选条件
        const cleanFiltersForCount = {
            trackingNumbers: filters.trackingNumbers,
            clientId: filters.clientId,
            exceptionStatus: filters.exceptionStatus,
            markExceptionAtFrom: filters.markExceptionAtFrom,
            markExceptionAtTo: filters.markExceptionAtTo,
            returnToWhsAtFrom: filters.returnToWhsAtFrom,
            returnToWhsAtTo: filters.returnToWhsAtTo,
            putawayAtFrom: filters.putawayAtFrom,
            putawayAtTo: filters.putawayAtTo,
            warehouseLocationCode: filters.warehouseLocationCode,
            storageNumber: filters.storageNumber,
        };

        // 刷新子tab计数
        refreshSubTabCounts(cleanFiltersForCount, activeTab);
    };

    // 处理选择变化
    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ExceptionOrderListItem[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    // 批量设置 Next Action
    const handleBatchSet = async () => {
        if (!batchActionValue || selectedRows.length === 0) {
            message.warning(t('wms.exceptions.messages.pleaseSelectOrdersAndAction'));
            return;
        }

        // 终态异常码列表（与单条编辑保持一致）
        // 与后端 ExceptionDictionaries.TERMINAL_EXCEPTION_CODES 保持一致
        const terminalExceptionCodes = [
            // 丢失类
            'ERHUB003', 'ERHUB005', 'ERHUB008', 'ERWHS003', 'ERWHS004', 'ERDSP003',
            // 破损类
            'ERHUB001', 'ERHUB006', 'ERHUB007', 'ERWHS001', 'ERWHS005', 'ERDSP001'
        ];

        // 订单终态状态列表
        // DELIVERED(60), RETURNED(80), RETURN_STARTED(81), DESTROYED(85), MISSING(86), DAMAGED(87)
        const terminalStatuses = [60, 80, 81, 85, 86, 87];

        // 检查是否有不允许编辑的异常单
        const disabledRows = selectedRows.filter(row => {
            // 1. 异常单已关闭
            if (row.exceptionStatus == 90) {
                return true;
            }
            // 2. 订单终态状态
            if (row.orderStatus && terminalStatuses.includes(row.orderStatus)) {
                return true;
            }
            // 3. 终态异常码
            if (row.exceptionReasonCode && terminalExceptionCodes.includes(row.exceptionReasonCode)) {
                return true;
            }
            return false;
        });

        if (disabledRows.length > 0) {
            message.warning(t('wms.exceptionorder.messages.onlyNewOrProcessedOrdersCanBeUpdated'));
            return;
        }

        try {
            // 使用批量接口
            const orderIds = selectedRows.map(row => parseInt(row.orderId));
            // 将数字 action 转换为枚举名称
            const actionName = ACTION_NAME_MAP[batchActionValue || ''];

            const result = await batchSetNextAction({
                variables: {
                    orderIds: orderIds,
                    actionName: actionName
                },
                // 修复：使用update函数手动更新Apollo缓存
                update: (cache) => {
                    // 清除相关查询的缓存，强制重新获取数据
                    cache.evict({ fieldName: 'queryExceptionOrdersByTab' });
                    cache.evict({ fieldName: 'queryExceptionOrderCountsByTabs' });
                    cache.gc(); // 垃圾回收，清理无效缓存
                }
            });

            const updatedCount = result.data?.batchSetNextAction.length || 0;
            
            // 获取国际化的动作文本 - 使用与单条设置相同的方法
            const actionText = getNextActionTextFromBackend(actionName);
            
            message.success(t('wms.exceptions.messages.successfullyUpdatedOrdersToAction', {
                count: updatedCount,
                action: actionText
            }));

            // 清空选择和批量操作
            setSelectedRowKeys([]);
            setSelectedRows([]);
            setBatchActionValue(undefined);

            // 修复：立即刷新数据，由于缓存已清除，会发起新的网络请求
            handleRefresh();
            
            // 手动延迟刷新计数，确保后端异步操作完成
            setTimeout(() => {
                refreshCountsWithFilters(filters);
            }, 1000);
        } catch (error) {
            console.error('Batch set next action failed:', error);
            showError(error);
        }
    };

    // 异步下载处理函数
    const handleDownload = async () => {
        const currentTime = Date.now();
        const lastTime = getLastDownloadTime();

        if (currentTime - lastTime < 5000) {
            const remainingTime = Math.ceil((5000 - (currentTime - lastTime)) / 1000);
            message.warning(`Please wait ${remainingTime} seconds before trying again`);
            return;
        }
        if (downloadLoading) {
            message.warning('Report is being generated, please do not click repeatedly');
            return;
        }
        // 下载需按仓鉴权（@RequireResourceAndWarehouse），必须先选中仓库
        if (!currentWarehouse?.id) {
            message.warning(t('wms.exceptions.messages.pleaseSelectWarehouse'));
            return;
        }

        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);

            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // 直接复用页面查询的 filters，与查询逻辑完全一致
            const downloadFilters: any = {
                ...filters,
                tab: activeTab,
                subTab: activeSubTab,
            };

            // 勾选下载：用选中行的运单号覆盖
            if (selectedRows && selectedRows.length > 0) {
                const trackingNumbers = selectedRows
                    .map(row => row.trackingNumber)
                    .filter((tn): tn is string => !!tn);
                if (trackingNumbers.length > 0) {
                    downloadFilters.trackingNumbers = trackingNumbers.join(';');
                }
            }

            const response = await generateReportMutation({
                variables: {
                    warehouseId: parseInt(currentWarehouse.id),
                    filters: downloadFilters,
                    timezone: userTimezone
                }
            });

            const fileName = response.data?.generateExceptionOrderDownloadReport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadCompleteModalVisible(true);
            } else {
                message.error('Failed to start report generation task');
            }
        } catch (error: any) {
            console.error('Report generation failed:', error);
            showError(error);
            setDownloadLoading(false);
        }
    };

    // 刷新数据 - 手动刷新列表和计数
    const handleRefresh = () => {
        // 先刷新列表数据
        handleFilter();
        
        // 手动刷新计数，使用当前的筛选条件
        refreshCountsWithFilters(filters);
    };

    // 显示包裹详情
    const showParcelDetail = (record: ExceptionOrderListItem) => {
        setDetailOrderId(record.orderId);
        setDetailVisible(true);
    };

    // 批量标记异常确认
    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {
        if (!orders || orders.length === 0) {
            message.warning(t('wms.exceptions.messages.selectAtLeastOneOrder'));
            return;
        }

        setBatchMarkLoading(true);
        const siteType = currentWarehouse?.siteType;
        
        try {
            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionHubId: siteType === SiteType.HUB ? currentWarehouse?.id : currentWarehouse?.parentSiteId,
                        exceptionWarehouseId: siteType === SiteType.WAREHOUSE ? currentWarehouse?.id : null,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || []
                    },
                    // 修复：清除缓存确保数据更新
                    update: (cache) => {
                        cache.evict({ fieldName: 'queryExceptionOrdersByTab' });
                        cache.evict({ fieldName: 'queryExceptionOrderCountsByTabs' });
                        cache.gc();
                    }
                })
            ));
            message.success(t('wms.exceptions.messages.batchMarkAsExceptionSuccess'));
            setBatchModalVisible(false);
            setSelectedRowKeys([]);
            setSelectedRows([]);
            handleRefresh(); // 刷新数据
        } catch (e) {
            console.error('Batch mark as exception failed:', e);
            message.error(t('wms.exceptions.messages.batchMarkAsExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };

    // 渲染司机退回的二级 Tab
    const renderDriverReturnSubTabs = () => {
        return (
            <Tabs activeKey={activeSubTab || 'all'} onChange={handleSubTabChange} type="card">
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.driverReturn.all'), 
                        tabCounts.driverReturnSubTabs.all,
                        loadingCounts
                    )} 
                    key="all" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.driverReturn.pendingPutaway'), 
                        tabCounts.driverReturnSubTabs.pendingPutaway,
                        loadingCounts
                    )} 
                    key="pending_putaway" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.driverReturn.putaway'), 
                        tabCounts.driverReturnSubTabs.putaway,
                        loadingCounts
                    )} 
                    key="putaway" 
                />
                <TabPane 
                    tab={getTabLabelWithBadge(
                        t('wms.exceptionorder.tabs.driverReturn.pendingPicking'), 
                        tabCounts.driverReturnSubTabs.pendingPicking,
                        loadingCounts
                    )} 
                    key="pending_picking" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.driverReturn.picked'), 
                        tabCounts.driverReturnSubTabs.picked,
                        loadingCounts
                    )} 
                    key="picked" 
                />
            </Tabs>
        );
    };

    // 渲染其他异常的二级 Tab
    const renderOtherExceptionsSubTabs = () => {
        return (
            <Tabs activeKey={activeSubTab || 'missing'} onChange={handleSubTabChange} type="card">
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.otherExceptions.missing'), 
                        tabCounts.otherExceptionsSubTabs.missing,
                        loadingCounts
                    )} 
                    key="missing" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.otherExceptions.longMissing'), 
                        tabCounts.otherExceptionsSubTabs.longMissing,
                        loadingCounts
                    )} 
                    key="long_missing" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.otherExceptions.damaged'), 
                        tabCounts.otherExceptionsSubTabs.damaged,
                        loadingCounts
                    )} 
                    key="damaged" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.otherExceptions.missort'), 
                        tabCounts.otherExceptionsSubTabs.missort,
                        loadingCounts
                    )} 
                    key="missort" 
                />
                <TabPane 
                    tab={getTabLabel(
                        t('wms.exceptionorder.tabs.otherExceptions.oversize'), 
                        tabCounts.otherExceptionsSubTabs.oversize,
                        loadingCounts
                    )} 
                    key="oversize" 
                />
            </Tabs>
        );
    };

    // 渲染批量操作区域
    const renderBatchOperationArea = () => {
        return (
            <div className="flex justify-between items-center mb-3">
                {/* 左侧：标记异常按钮 + 下一步动作选择器 + 设置按钮 */}
                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        onClick={() => {
                            if (selectedRows.length === 0) {
                                message.warning(t('wms.exceptions.messages.selectAtLeastOneOrder'));
                                return;
                            }
                            setBatchModalVisible(true);
                        }}
                        disabled={selectedRowKeys.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.exceptions.buttons.markAsException')}</span>
                    </Button>
                    <span className="mr-2 text-gray-800 font-semibold">{t('wms.exceptions.filters.nextAction')}</span>
                    <Select
                        placeholder={t('components.input.select')}
                        value={batchActionValue}
                        onChange={setBatchActionValue}
                        style={{ width: 140, marginRight: 8 }}
                        options={ACTION_OPTIONS}
                        disabled={selectedRowKeys.length === 0}
                        allowClear
                    />
                    <Button
                        type="primary"
                        onClick={handleBatchSet}
                        disabled={selectedRowKeys.length === 0 || !batchActionValue}
                        loading={batchSetLoading}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.exceptions.buttons.set')}</span>
                    </Button>
                </div>

                {/* 右侧：下载、筛选、重置按钮 */}
                <div className="flex items-center gap-2">
                    <Button
                        type="primary"
                        onClick={handleDownload}
                        loading={cooldownRemaining > 0}
                        disabled={downloadLoading || cooldownRemaining > 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">
                            {cooldownRemaining > 0
                                ? `${t('wms.exceptions.buttons.download')} ${cooldownRemaining}s`
                                : t('wms.exceptions.buttons.download')
                            }
                        </span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.exceptions.filters.filter')}</span>
                    </Button>
                    <Button
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.exceptions.filters.reset')}</span>
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <style>{`
                .exception-order-page .ant-tabs-top > .ant-tabs-nav {
                    margin-bottom: 0;
                }
                .exception-order-page .ant-tabs-content-holder {
                    padding-top: 0;
                }
            `}</style>
            {/* 一级 Tab */}
            <div className="exception-order-page">
                <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
                    <TabPane
                        tab={getTabLabel(
                            t('wms.exceptionorder.tabs.warehouseSuspectedLost'),
                            tabCounts.warehouseSuspectedLost,
                            loadingCounts
                        )}
                        key={ExceptionTab.WAREHOUSE_SUSPECTED_LOST}
                    >
                        <ExceptionOrderFilter
                            filters={filters}
                            onFilterChange={setFilters}
                            onSearch={handleFilter}
                            onReset={handleResetFilters}
                            currentTab={activeTab}
                            currentSubTab={activeSubTab}
                        />

                        {renderBatchOperationArea()}

                        <ExceptionOrderTable
                            data={response?.items || []}
                            loading={loading}
                            currentTab={activeTab}
                            currentSubTab={activeSubTab}
                            pagination={{
                                current: (filters.pageNumber || 0) + 1,
                                pageSize: filters.pageSize || 20,
                                total: response?.totalCount || 0,
                                onChange: handlePaginationChange,
                            }}
                            onSelectChange={handleSelectChange}
                            selectedRowKeys={selectedRowKeys}
                            onRefresh={handleRefresh}
                            showParcelDetail={showParcelDetail}
                        />
                    </TabPane>

                    <TabPane
                        tab={getTabLabel(
                            t('wms.exceptionorder.tabs.driverReturn.label'),
                            tabCounts.driverReturn,
                            loadingCounts
                        )}
                        key={ExceptionTab.DRIVER_RETURN}
                    >
                        {/* 二级 Tab */}
                        {renderDriverReturnSubTabs()}

                        <div className="mt-4">
                            <ExceptionOrderFilter
                                filters={filters}
                                onFilterChange={setFilters}
                                onSearch={handleFilter}
                                onReset={handleResetFilters}
                                currentTab={activeTab}
                                currentSubTab={activeSubTab}
                            />

                            {renderBatchOperationArea()}

                            <ExceptionOrderTable
                                data={response?.items || []}
                                loading={loading}
                                currentTab={activeTab}
                                currentSubTab={activeSubTab}
                                pagination={{
                                    current: (filters.pageNumber || 0) + 1,
                                    pageSize: filters.pageSize || 20,
                                    total: response?.totalCount || 0,
                                    onChange: handlePaginationChange,
                                }}
                                onSelectChange={handleSelectChange}
                                selectedRowKeys={selectedRowKeys}
                                onRefresh={handleRefresh}
                                showParcelDetail={showParcelDetail}
                            />
                        </div>
                    </TabPane>

                    <TabPane
                        tab={getTabLabel(
                            t('wms.exceptionorder.tabs.otherExceptions.label'),
                            tabCounts.otherExceptions,
                            loadingCounts
                        )}
                        key={ExceptionTab.OTHER_EXCEPTIONS}
                    >
                        {/* 二级 Tab */}
                        {renderOtherExceptionsSubTabs()}

                        <div className="mt-4">
                            <ExceptionOrderFilter
                                filters={filters}
                                onFilterChange={setFilters}
                                onSearch={handleFilter}
                                onReset={handleResetFilters}
                                currentTab={activeTab}
                                currentSubTab={activeSubTab}
                            />

                            {renderBatchOperationArea()}

                            <ExceptionOrderTable
                                data={response?.items || []}
                                loading={loading}
                                currentTab={activeTab}
                                currentSubTab={activeSubTab}
                                pagination={{
                                    current: (filters.pageNumber || 0) + 1,
                                    pageSize: filters.pageSize || 20,
                                    total: response?.totalCount || 0,
                                    onChange: handlePaginationChange,
                                }}
                                onSelectChange={handleSelectChange}
                                selectedRowKeys={selectedRowKeys}
                                onRefresh={handleRefresh}
                                showParcelDetail={showParcelDetail}
                            />
                        </div>
                    </TabPane>
                </Tabs>
            </div>

            {/* 包裹详情抽屉组件 */}
            <CentralParcelDetailDrawer
                showInterceptionExemptionAlert={true}
                visible={detailVisible}
                onClose={() => setDetailVisible(false)}
                orderId={detailOrderId}
                userRole={userRole}
            />

            {/* 批量标记异常弹窗 */}
            <BatchMarkAsExceptionModal
                visible={batchModalVisible}
                parcels={selectedRows.map(row => ({
                    orderId: Number(row.orderId),
                    trackingNumber: row.trackingNumber
                }))}
                onOk={handleBatchMarkOk}
                onCancel={() => setBatchModalVisible(false)}
                loading={batchMarkLoading}
                userRole={userRole}
                exceptionSelectData={exceptionSelectData}
            />
            {/* 下载完成提示弹窗 */}
            <ParcelsDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default ExceptionOrderPage;
